import React from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { IntlProvider } from "react-intl";
import Chatbot from "./components/Chatbot/Chatbot.jsx";
import Login from "./components/Login/Login.jsx";
import UserProfile from "./components/UserProfile/UserProfile.jsx";
import Dashboard from "./components/Dashboard/Dashboard.jsx";
import Messageboard from "./components/Messageboard/Messageboard.jsx";
import useAppSession from "./stores/useAppSession.js";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AdminLayout from "./components/AdminLayout/AdminLayout.jsx";
import Workspace from "./components/Workspace/Workspace.jsx";
import ExpenseManagement from "./components/ExpenseManagement/ExpenseManagement.jsx";
import Whatsapp from "./components/whatsapp/whatsapp.jsx";
import ContractsViewer from "./components/contratos/contratos.jsx";
import messagesPt from "./locales/pt-br.json";
import messagesEn from "./locales/en-US.json";
import Widget from "./components/Widget/Widget.jsx";

const messages = {
  pt: messagesPt,
  en: messagesEn,
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { roles } = useAppSession();
  const location = useLocation();
  const hasAccess = roles?.some((role) => allowedRoles.includes(role));

  if (!hasAccess) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
};

const RoutesComponent = ({ onLanguageChange }) => {
  const location = useLocation();
  const {
    token,
    clientId,
    sessionId,
    roles,
    setToken,
    setRoles,
    setClientId,
    setSessionId,
    clearSession,
  } = useAppSession();
  const navigate = useNavigate();

  const handleLoginSuccess = (token, client_id, userRoles) => {
    setToken(token);
    setClientId(client_id);

    setRoles(Array.isArray(userRoles) ? userRoles.map((role_string) => role_string.toUpperCase()) : ["CHAT"]);
    localStorage.setItem("token", token);
    navigate("/dashboard");
  };

  const handleLogout = () => {
    localStorage.setItem("token", "");
    clearSession();
    window.location.reload();
  };

  const handleSessionId = (session) => {
    setSessionId(session);
  };

  if (!token && location.pathname !== "/widget") {
    return (
      <>
        <Login
          onLoginSuccess={handleLoginSuccess}
          onLanguageChange={onLanguageChange}
        />
        <ToastContainer />
      </>
    );
  }

  return (
    <>
      <ToastContainer />

      <Routes >
        <Route
          path="/login"
          element={
            <Login
              onLoginSuccess={handleLoginSuccess}
              onLanguageChange={onLanguageChange}
            />
          }
        />

        <Route
          path="/"
          element={
            <AdminLayout>
              <Chatbot onSessionId={handleSessionId} onLogout={handleLogout} />
            </AdminLayout>
          }
        />

        <Route
          path="/user-profile"
          element={
            <AdminLayout>
              <UserProfile onLogout={handleLogout} />
            </AdminLayout>
          }
        />

        <Route
          path="/dashboard"
          element={
            <AdminLayout>
              <Dashboard onLogout={handleLogout} />
            </AdminLayout>
          }
        />

        <Route
          path="/messageboard"
          element={
            <ProtectedRoute allowedRoles={["ADMIN", "ADMIN_IA","USER"]}>
              <AdminLayout>
                <Messageboard onLogout={handleLogout} />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/workspace"
          element={
            <ProtectedRoute allowedRoles={["ADMIN", "ADMIN_IA"]}>
              <AdminLayout>
                <Workspace />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/expense-management"
          element={
            <ProtectedRoute allowedRoles={["ADMIN", "ADMIN_IA", "ACC"]}>
              <AdminLayout>
                <ExpenseManagement />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/whatsapp"
          element={
            <ProtectedRoute allowedRoles={["ADMIN", "ADMIN_IA"]}>
              <AdminLayout>
                <Whatsapp />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/contratos"
          element={
            <ProtectedRoute allowedRoles={["ADMIN", "ADMIN_IA"]}>
              <AdminLayout>
                <ContractsViewer />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/widget"
          element={
            <ProtectedRoute allowedRoles={["ADMIN", "ADMIN_IA"]}>
              <AdminLayout>
                <Widget />
              </AdminLayout>
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </>
  );
};

const App = () => {
  const { locale, setLocale } = useAppSession();

  const handleLanguageChange = (language) => {
    setLocale(language);
  };

  return (
    <IntlProvider onError={() => { return; }} locale={locale} messages={messages[locale]}>
      <Router >
        <RoutesComponent onLanguageChange={handleLanguageChange} />
        <ToastContainer />
      </Router>
    </IntlProvider>
  );
};

export default App;